import { useEffect, useState } from 'react';
import { Mark } from './icons';
import { Spinner } from './components/States';
import { ToastProvider } from './components/Toast';
import { Login } from './auth/Login';
import { OwnerApp } from './owner/OwnerApp';
import { OwnerMobile } from './owner/OwnerMobile';
import { TenantApp } from './tenant/TenantApp';
import { api, isUnauthorized } from './api';
import type { Me } from './api';

type AuthState =
  | { phase: 'loading' }
  | { phase: 'anon' }
  | { phase: 'authed'; me: Me };

/** True when the viewport is phone-width (owner gets the mobile surface). */
function useIsMobile(breakpoint = 720): boolean {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < breakpoint,
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

export function App() {
  const [auth, setAuth] = useState<AuthState>({ phase: 'loading' });
  const isMobile = useIsMobile();

  // Bootstrap: resolve the session from the cookie. API down → treat as anon
  // (Login), never crash.
  useEffect(() => {
    let alive = true;
    api
      .me()
      .then((me) => alive && setAuth({ phase: 'authed', me }))
      .catch((err) => {
        if (!alive) return;
        if (isUnauthorized(err)) setAuth({ phase: 'anon' });
        else setAuth({ phase: 'anon' }); // network / API-down → show Login
      });
    return () => {
      alive = false;
    };
  }, []);

  // Once authed with no route yet, drop onto a sensible default screen —
  // owner → Overview, tenant → Home — without adding a phantom history entry.
  useEffect(() => {
    if (auth.phase !== 'authed') return;
    if (window.location.hash) return;
    const isOwner = auth.me.role === 'owner' || auth.me.role === 'staff';
    const url = window.location.pathname + window.location.search + (isOwner ? '#/overview' : '#/home');
    window.history.replaceState(null, '', url);
  }, [auth]);

  async function logout() {
    try {
      await api.logout();
    } catch {
      // ignore — clear locally regardless
    }
    // Drop the route so the next sign-in starts from its own default.
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    setAuth({ phase: 'anon' });
  }

  if (auth.phase === 'loading') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
        <Mark size={44} />
        <Spinner />
      </div>
    );
  }

  if (auth.phase === 'anon') {
    return (
      <ToastProvider>
        <Login onAuthed={(me) => setAuth({ phase: 'authed', me })} />
      </ToastProvider>
    );
  }

  const { me } = auth;
  const isOwner = me.role === 'owner' || me.role === 'staff';
  const onMeChange = (next: Me) => setAuth({ phase: 'authed', me: next });

  return (
    <ToastProvider>
      {isOwner ? (
        isMobile ? (
          <div className="mobile-wrap">
            <OwnerMobile me={me} onLogout={logout} onMeChange={onMeChange} />
          </div>
        ) : (
          <OwnerApp me={me} onLogout={logout} onMeChange={onMeChange} />
        )
      ) : (
        <div className="mobile-wrap">
          <TenantApp me={me} onLogout={logout} />
        </div>
      )}
    </ToastProvider>
  );
}
